import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Box,
  Chip,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExportIcon } from '../../../utils/icons';
import DropZone from '../../files/dropzone';
import PermissionsHelperDialog from './PermissionsHelperDialog';
import {
  UserImportService,
  UserImportData,
  ImportResults,
  ExistingUser,
} from '../services/UserImportService';

const PreviewDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    maxWidth: '90vw',
    width: '900px',
    maxHeight: '80vh',
  },
  '& .MuiDialogContent-root': {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 24px',
  },
});

const PreviewTable = styled(Table)({
  '& .MuiTableCell-root': {
    fontSize: '0.875rem',
  },
});

const ActionChip = styled(Chip)({
  fontWeight: 'bold',
  fontSize: '0.75rem',
});

const OldValue = styled('div')({
  color: '#d32f2f',
  fontSize: '0.85rem',
  marginBottom: '4px',
  textDecoration: 'line-through',
});

const NewValue = styled('div')({
  fontWeight: 500,
  fontSize: '0.85rem',
});

const ProgressContainer = styled('div')({
  padding: '20px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
});

const ProgressItem = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: '8px 0',
  padding: '8px 12px',
  backgroundColor: '#fafafa',
  borderRadius: '4px',
});

const SummaryBox = styled(Box)({
  marginBottom: '16px',
});

interface UserImportDialogProps {
  open: boolean;
  onClose: () => void;
  existingUsers: ExistingUser[];
  allPermissions: string[];
  loading?: boolean;
  onImportComplete: () => void;
}

type Step = 'instructions' | 'preview' | 'processing' | 'results';

const UserImportDialog: React.FC<UserImportDialogProps> = ({
  open,
  onClose,
  existingUsers,
  allPermissions,
  loading,
  onImportComplete,
}) => {
  const [step, setStep] = useState<Step>('instructions');
  const [importData, setImportData] = useState<UserImportData | null>(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [permissionsHelperOpen, setPermissionsHelperOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open]);

  const resetState = () => {
    setStep('instructions');
    setImportData(null);
    setImportProgress(0);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (step === 'processing') return;
    resetState();
    onClose();
  };

  const handleFileSelected = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    try {
      const records = await UserImportService.parseCSV(file);
      const data = UserImportService.validateAndCategorize(records, existingUsers, allPermissions);
      setImportData(data);
      setStep('preview');
    } catch (error: any) {
      setImportData({
        toInvite: [],
        toUpdate: [],
        skipped: [],
        errors: [{ row: 0, message: `Failed to parse CSV: ${error.message}` }],
      });
      setStep('preview');
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelected(Array.from(files));
    }
  };

  const handleImport = async () => {
    if (!importData) return;
    setStep('processing');
    setImportProgress(0);

    try {
      const results = await UserImportService.processImport(importData, (percent) => {
        setImportProgress(percent);
      });

      setImportResults(results);
      setStep('results');
      onImportComplete();
    } catch (error: any) {
      setImportResults({
        success: [],
        errors: [`Import failed unexpectedly: ${error.message}`],
      });
      setStep('results');
    }
  };

  const hasChanges = importData && (
    importData.toInvite.length > 0 || importData.toUpdate.length > 0
  );

  const getDialogTitle = () => {
    switch (step) {
      case 'instructions': return 'Import Users';
      case 'preview': return 'Import Preview';
      case 'processing': return 'Processing Import';
      case 'results': return 'Import Complete';
    }
  };

  if (!open) return null;

  return (
    <>
      {step === 'instructions' ? (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>Import Users</DialogTitle>
          {loading ? (
            <DialogContent dividers>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <CircularProgress size={48} />
                <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
                  Loading users...
                </Typography>
              </Box>
            </DialogContent>
          ) : (
          <DialogContent dividers>
            <Grid container spacing={3}>
              {/* Step 1 */}
              <Grid size={12}>
                <Paper variant="outlined" style={{ padding: '16px' }}>
                  <Typography variant="h6" gutterBottom>Step 1: Download Template</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Download the CSV template to ensure your file is formatted correctly.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ExportIcon style={{ height: '18px', width: '18px' }} />}
                    onClick={() => UserImportService.generateTemplate()}
                  >
                    Download Template
                  </Button>
                </Paper>
              </Grid>

              {/* Step 2 */}
              <Grid size={12}>
                <Paper variant="outlined" style={{ padding: '16px' }}>
                  <Typography variant="h6" gutterBottom>Step 2: Understand Your Fields</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Field Name</strong></TableCell>
                          <TableCell><strong>Description</strong></TableCell>
                          <TableCell><strong>Required</strong></TableCell>
                          <TableCell><strong>Example</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Email of the user (cannot be changed via import)</TableCell>
                          <TableCell><Chip size="small" label="Required" color="primary" /></TableCell>
                          <TableCell><code>"user@company.com"</code></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>First Name</TableCell>
                          <TableCell>User's first name</TableCell>
                          <TableCell><Chip size="small" label="Optional" variant="outlined" /></TableCell>
                          <TableCell><code>"Jane"</code></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Last Name</TableCell>
                          <TableCell>User's last name</TableCell>
                          <TableCell><Chip size="small" label="Optional" variant="outlined" /></TableCell>
                          <TableCell><code>"Smith"</code></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell>Title of the user in the organization</TableCell>
                          <TableCell><Chip size="small" label="Optional" variant="outlined" /></TableCell>
                          <TableCell><code>"Onboarding Manager"</code></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Permissions</TableCell>
                          <TableCell>
                            Internal permission names separated by semicolons.
                            <br />
                            <Link component="button" variant="body2" sx={{ textAlign: 'left' }} onClick={() => setPermissionsHelperOpen(true)}>
                              View available permissions
                            </Link>
                          </TableCell>
                          <TableCell><Chip size="small" label="Optional" variant="outlined" /></TableCell>
                          <TableCell><code>"users/manage; orders/manage"</code></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* Step 3 */}
              <Grid size={12}>
                <Paper variant="outlined" style={{ padding: '16px' }}>
                  <Typography variant="h6" gutterBottom>Step 3: Import Rules</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}><strong>How the import works:</strong></Typography>
                  <ul style={{ marginLeft: '20px' }}>
                    <li><Typography variant="body2">If a user with an email is <strong>not found, an invite will be sent</strong> to that email</Typography></li>
                    <li><Typography variant="body2">If a user with an email <strong>exists, it will be updated</strong> with fields on the upload</Typography></li>
                    <li><Typography variant="body2">Fields that are <strong>left blank should remain unchanged</strong></Typography></li>
                    <li><Typography variant="body2"><strong>Email cannot be changed</strong> via import — it is used to match existing users</Typography></li>
                  </ul>
                  <Alert severity="info" style={{ marginTop: '12px' }}>
                    Use quotes around fields containing commas. Multiple permissions should be separated by semicolons (;).
                  </Alert>
                </Paper>
              </Grid>

              {/* Step 4 */}
              <Grid size={12}>
                <Paper variant="outlined" style={{ padding: '16px' }}>
                  <Typography variant="h6" gutterBottom>Step 4: Select Your File</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Drag and drop your CSV file here, or click to browse.
                  </Typography>
                  <input
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                  />
                  <DropZone
                    accept=".csv"
                    style={{ width: '100%', height: '10em', border: '1px dashed #93AAFD', borderRadius: '4px' }}
                    onDrop={(acceptedFiles: File[]) => {
                      if (acceptedFiles && acceptedFiles.length > 0) {
                        handleFileSelected(acceptedFiles);
                      }
                    }}
                  >
                    {({ isDragAccept, isDragReject }: { isDragAccept: boolean; isDragReject: boolean }) => {
                      if (isDragAccept || isDragReject) return 'Drop here to upload this file.';
                      return 'Drop a CSV file here or click to choose a file.';
                    }}
                  </DropZone>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          )}
          <DialogActions>
            <Button variant="text" onClick={handleClose}>Cancel</Button>
          </DialogActions>
        </Dialog>
      ) : (
        <PreviewDialog open={open} onClose={handleClose} maxWidth={false} fullWidth={false}>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogContent dividers>
            {step === 'results' && importResults ? (
              <ProgressContainer style={{ minHeight: 0 }}>
                <Typography variant="h6" gutterBottom>Import Complete</Typography>
                <SummaryBox>
                  <Typography variant="body1" style={{ marginBottom: '8px' }}>
                    <strong>Summary:</strong> {importResults.success.length} successful, {importResults.errors.length} failed
                  </Typography>
                  {importResults.errors.length > 0 && (
                    <Alert severity="warning" style={{ marginBottom: '8px' }}>
                      {importResults.errors.length} operation(s) failed. Check the details below.
                    </Alert>
                  )}
                  {importResults.success.length > 0 && importResults.errors.length === 0 && (
                    <Alert severity="success">All operations completed successfully!</Alert>
                  )}
                </SummaryBox>
                <Box style={{ flexGrow: 1, overflow: 'auto', marginTop: '16px', textAlign: 'left' }}>
                  {importResults.success.map((msg, index) => {
                    const [action, ...rest] = msg.split(':');
                    const detail = rest.join(':').trim();
                    return (
                      <ProgressItem key={`success-${index}`}>
                        <Typography variant="body2">{detail || msg}</Typography>
                        <Typography color="primary">&#10003; {action || 'Completed'}</Typography>
                      </ProgressItem>
                    );
                  })}
                  {importResults.errors.map((msg, index) => {
                    const [failedPart, ...errorParts] = msg.split(':');
                    const errorMessage = errorParts.join(':').trim();
                    return (
                      <ProgressItem key={`error-${index}`} style={{ backgroundColor: '#ffebee' }}>
                        <Typography variant="body2">{failedPart}</Typography>
                        <Typography color="error" style={{ fontSize: '0.875rem' }}>&#10007; {errorMessage || 'Failed'}</Typography>
                      </ProgressItem>
                    );
                  })}
                </Box>
              </ProgressContainer>
            ) : step === 'processing' ? (
              <ProgressContainer style={{ minHeight: 0 }}>
                <Typography variant="h6" gutterBottom>Processing Import...</Typography>
                <LinearProgress variant="determinate" value={importProgress} style={{ marginBottom: '20px', height: '14px', borderRadius: '4px' }} />
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                  {Math.round(importProgress)}% Complete
                </Typography>
                {importData && (
                  <Box style={{ flexGrow: 1, overflow: 'auto', textAlign: 'left' }}>
                    {importData.toInvite.map((rec, index) => (
                      <ProgressItem key={`invite-${index}`}>
                        <Typography variant="body2">Inviting: {rec.email}</Typography>
                        <Typography color="textSecondary">Processing...</Typography>
                      </ProgressItem>
                    ))}
                    {importData.toUpdate.map((rec, index) => (
                      <ProgressItem key={`update-${index}`}>
                        <Typography variant="body2">Updating: {rec.email}</Typography>
                        <Typography color="textSecondary">Processing...</Typography>
                      </ProgressItem>
                    ))}
                  </Box>
                )}
              </ProgressContainer>
            ) : step === 'preview' && importData ? (
              <>
                {importData.errors.length > 0 && (
                  <Alert severity="error" style={{ marginBottom: '16px' }}>
                    {importData.errors.length} row(s) have validation errors and will be skipped:
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      {importData.errors.map((err, i) => (
                        <li key={i}>Row {err.row}: {err.message}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
                {(importData.toInvite.length > 0 || importData.toUpdate.length > 0 || importData.skipped.length > 0 || importData.errors.length > 0) ? (
                  <>
                    <Typography variant="body1" gutterBottom>
                      The following changes will be applied based on your CSV file:
                    </Typography>
                    <Grid container spacing={2} style={{ marginTop: '8px', marginBottom: '16px' }}>
                      {importData.toInvite.length > 0 && (
                        <Grid>
                          <ActionChip label={`${importData.toInvite.length} to invite`} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                        </Grid>
                      )}
                      {importData.toUpdate.length > 0 && (
                        <Grid>
                          <ActionChip label={`${importData.toUpdate.length} to change`} style={{ backgroundColor: '#e3f2fd', color: '#1565c0' }} />
                        </Grid>
                      )}
                      {importData.skipped.length > 0 && (
                        <Grid>
                          <ActionChip label={`${importData.skipped.length} to skip`} style={{ backgroundColor: '#f5f5f5', color: '#616161' }} />
                        </Grid>
                      )}
                      {importData.errors.length > 0 && (
                        <Grid>
                          <ActionChip label={`${importData.errors.length} error(s)`} style={{ backgroundColor: '#ffebee', color: '#c62828' }} />
                        </Grid>
                      )}
                    </Grid>
                  </>
                ) : importData.errors.length === 0 ? (
                  <Alert severity="info">No changes to apply. All users in the imported file match the current data.</Alert>
                ) : null}
                {(importData.toInvite.length > 0 || importData.toUpdate.length > 0) && (
                  <TableContainer component={Paper} style={{ maxHeight: '300px', marginTop: '16px' }}>
                    <PreviewTable stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell>Permissions</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importData.toInvite.map((rec, index) => (
                          <TableRow key={`invite-${index}`}>
                            <TableCell><Typography variant="body2" style={{ fontWeight: 500 }}>{rec.email}</Typography></TableCell>
                            <TableCell><NewValue>{[rec.firstName, rec.lastName].filter(Boolean).join(' ') || '\u2014'}</NewValue></TableCell>
                            <TableCell><NewValue>{rec.title || '\u2014'}</NewValue></TableCell>
                            <TableCell><NewValue>{rec.permissions?.join('; ') || '\u2014'}</NewValue></TableCell>
                            <TableCell align="center">
                              <Chip size="small" label="invite" style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }} />
                            </TableCell>
                          </TableRow>
                        ))}
                        {importData.toUpdate.map((rec, index) => {
                          const nameChanged = (rec.firstName && rec.firstName !== rec.currentValues.firstName) || (rec.lastName && rec.lastName !== rec.currentValues.lastName);
                          const oldName = [rec.currentValues.firstName, rec.currentValues.lastName].filter(Boolean).join(' ');
                          const newName = [rec.firstName || rec.currentValues.firstName, rec.lastName || rec.currentValues.lastName].filter(Boolean).join(' ');
                          const titleChanged = rec.title && rec.title !== rec.currentValues.title;
                          const permsChanged = rec.permissions && JSON.stringify([...(rec.currentValues.permissions || [])].sort()) !== JSON.stringify([...rec.permissions].sort());
                          return (
                          <TableRow key={`update-${index}`}>
                            <TableCell><Typography variant="body2" style={{ fontWeight: 500 }}>{rec.email}</Typography></TableCell>
                            <TableCell>
                              {nameChanged ? (
                                <>
                                  <OldValue>{oldName}</OldValue>
                                  <NewValue>{newName}</NewValue>
                                </>
                              ) : (
                                <Typography variant="body2">{oldName || '\u2014'}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {titleChanged ? (
                                <>
                                  <OldValue>{rec.currentValues.title || '\u2014'}</OldValue>
                                  <NewValue>{rec.title}</NewValue>
                                </>
                              ) : (
                                <Typography variant="body2">{rec.currentValues.title || '\u2014'}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {permsChanged ? (
                                <>
                                  <OldValue>{rec.currentValues.permissions?.join('; ') || '\u2014'}</OldValue>
                                  <NewValue>{rec.permissions!.join('; ')}</NewValue>
                                </>
                              ) : (
                                <Typography variant="body2">{rec.currentValues.permissions?.join('; ') || '\u2014'}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip size="small" label="change" style={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }} />
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </PreviewTable>
                  </TableContainer>
                )}
              </>
            ) : null}
          </DialogContent>
          <DialogActions>
            {step === 'results' ? (
              <Button onClick={handleClose} variant="contained" color="primary">Close</Button>
            ) : (
              <>
                <Button variant="text" onClick={handleClose} disabled={step === 'processing'}>Cancel</Button>
                {step === 'preview' && (
                  <Button onClick={handleImport} variant="contained" color="primary" disabled={!hasChanges}>Import</Button>
                )}
              </>
            )}
          </DialogActions>
        </PreviewDialog>
      )}
      <PermissionsHelperDialog open={permissionsHelperOpen} onClose={() => setPermissionsHelperOpen(false)} />
    </>
  );
};

export default UserImportDialog;
